import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail } from "lucide-react";
import { BrandIcon, BrandLogo } from "@/components/Brand";
import { API_BASE } from "@/lib/api";
import { PRIVACY } from "./ApplyCopy";
import { Shell } from "./Apply";

/** The privacy notice behind the consent tick. Public, no login, both languages — and the Hindi
 *  says plainly that it is a translation awaiting review. */
export default function ApplyPrivacy() {
  const [lang, setLang] = useState("en");
  const [info, setInfo] = useState(null);
  const t = PRIVACY[lang];

  useEffect(() => {
    axios.get(`${API_BASE}/public/apply/privacy`).then(({ data }) => setInfo(data)).catch(() => setInfo({}));
  }, []);

  return (
    <Shell header={(
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5"><BrandIcon /><BrandLogo className="h-5" /></div>
        <button type="button" onClick={() => setLang(lang === "en" ? "hi" : "en")}
          data-testid="privacy-lang-toggle"
          className="rounded-full border-2 px-3.5 py-1.5 text-[13px] font-semibold hover:bg-accent">
          {lang === "en" ? "हिंदी" : "English"}
        </button>
      </div>
    )}>
      <div className="mt-5" data-testid="apply-privacy">
        {t.review && (
          <p className="mb-3 inline-flex rounded-full bg-muted px-3 py-1 text-[12px] font-medium text-muted-foreground" data-testid="privacy-review-note">
            {t.review}
          </p>
        )}
        <h1 className="font-display text-2xl font-bold leading-tight">{t.title}</h1>
        <p className="mt-1.5 text-[15px] text-muted-foreground">{t.updated}</p>
        <div className="mt-6 space-y-5">
          {t.sections.map(([head, body]) => (
            <section key={head} data-testid={`privacy-section-${head.slice(0, 12)}`}>
              <h2 className="text-base font-semibold">{head}</h2>
              <p className="mt-1 text-[14px] leading-relaxed text-muted-foreground">{body}</p>
            </section>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border-2 border-brand-yellow/50 bg-brand-yellow/10 p-4" data-testid="privacy-grievance">
          <p className="text-[12px] font-semibold uppercase tracking-wider text-brand-yellow-ink">
            {lang === "en" ? "Grievance contact" : "शिकायत संपर्क"}
          </p>
          {info?.grievance_email ? (
            <a href={`mailto:${info.grievance_email}`} className="mt-1.5 flex items-center gap-2 font-medium underline" data-testid="privacy-grievance-email">
              <Mail className="h-4 w-4" />{info.grievance_email}
            </a>
          ) : (
            <p className="mt-1.5 text-[14px] text-muted-foreground" data-testid="privacy-grievance-unset">
              {lang === "en" ? "Not set yet — applications are not open." : "अभी तय नहीं — आवेदन बंद हैं।"}
            </p>
          )}
          {info?.grievance_name && <p className="text-[13px] text-muted-foreground">{info.grievance_name}</p>}
          <p className="mt-2 text-[13px] text-muted-foreground">
            {lang === "en"
              ? `Details are kept for ${info?.retention_months || 12} months from your last activity.`
              : `जानकारी आख़िरी गतिविधि से ${info?.retention_months || 12} महीने तक रखी जाती है।`}
          </p>
        </div>
        <Link to="/apply" className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-medium underline" data-testid="privacy-back">
          <ArrowLeft className="h-3.5 w-3.5" />{lang === "en" ? "Back to the form" : "फ़ॉर्म पर वापस"}
        </Link>
      </div>
    </Shell>
  );
}
